import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'modules/auth/context';
import * as TestModule from 'modules/test';
import { usePublicTestCategory } from 'modules/test/hooks/usePublicTestCategory';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { PublicTestsCatalog } from './PublicTestsCatalog';
import { PortalHeading } from './PortalHeading';
import { CategoryHub, hubCategories } from './CategoryHub';
import { CategoryHero } from './CategoryHero';
import { CategoryPills } from './CategoryPills';
import { catalogCategories, localizedLabel, localizedSubjectLabel } from './catalogCategories';
import { PortalPaymentReturn } from 'components/portal/PortalPaymentReturn';
import { PortalRenewalBanner } from 'components/portal/PortalRenewalBanner';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';

/** Filters that only make sense on the test list: any of them keeps the learner off the category cards. */
const LIST_PARAMS = ['search', 'subject', 'difficulty', 'access'] as const;

export default function TestsCatalog() {
  const { t, i18n } = useTranslation();
  const { state, methods } = useAuthContext();
  const [params, setParams] = useSearchParams();
  const { data: subscription } = usePortalSubscription();
  const [renewalModalOpen, setRenewalModalOpen] = useState(false);
  const plan = params.get('plan');
  const paymentOrder = params.get('paymentOrder');
  const selectedPlan = plan === 'START' || plan === 'PRO' || plan === 'PREMIUM' ? plan : null;

  const { data: categoriesResponse } = TestModule.Hooks.usePublicCategories();
  const dynamicCategories = categoriesResponse?.data ?? [];
  const { selectedCategory, setSelectedCategory } = usePublicTestCategory(dynamicCategories);
  const activeCategory = dynamicCategories.find((c) => c.id.toLowerCase() === selectedCategory.toLowerCase());
  const language = i18n?.language;

  // Cards for the API's categories; before they load, the built-in list stands in without counts.
  const cards = dynamicCategories.length > 0
    ? hubCategories(
        dynamicCategories,
        (category) => localizedLabel(category, language),
        (subject) => localizedSubjectLabel(subject, language),
      )
    : catalogCategories(undefined, t, language)
        .filter(({ id }) => id !== 'all')
        .map(({ id, label }) => ({ id, label, subjectLabels: [] as string[] }));

  const onHub = selectedCategory === 'all' && LIST_PARAMS.every((key) => !params.get(key));

  const closeCheckout = () => {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('plan');
      return next;
    }, { replace: true });
  };

  const searchAcrossCategories = (query: string) =>
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('search', query);
      next.delete('page');
      return next;
    });

  const leaveCategory = () =>
    setParams(previous => {
      const next = new URLSearchParams(previous);
      ['category', 'page', ...LIST_PARAMS].forEach((key) => next.delete(key));
      return next;
    });

  return (
    <div className="flex flex-col gap-5">
      <PortalHeading title={t('portal.tests')} description={t('portal.tests_description')} />
      {paymentOrder && <PortalPaymentReturn orderId={paymentOrder} onBalance={balanceSom => {
        methods.setUser(user => (user ? { ...user, balanceSom } : user));
      }} onDismiss={() => {
        setParams(previous => {
          const next = new URLSearchParams(previous);
          next.delete('paymentOrder');
          return next;
        }, { replace: true });
      }} />}
      <PortalRenewalBanner
        subscription={subscription}
        onRenew={() => setRenewalModalOpen(true)}
      />
      {onHub ? (
        <CategoryHub categories={cards} onOpen={setSelectedCategory} onSearch={searchAcrossCategories} />
      ) : (
        <>
          {selectedCategory !== 'all' && (
            <>
              <CategoryPills categories={cards} activeId={selectedCategory} onSelect={setSelectedCategory} />
              <CategoryHero
                categoryId={selectedCategory}
                label={cards.find(({ id }) => id.toLowerCase() === selectedCategory.toLowerCase())?.label ?? selectedCategory}
                testCount={activeCategory?.testCount}
                subjectCount={activeCategory?.subjects?.length ?? 0}
                onBack={leaveCategory}
              />
            </>
          )}
          <PublicTestsCatalog userBalance={state.user?.balanceSom ?? 0} showCategoryChips={selectedCategory === 'all'} />
        </>
      )}
      {selectedPlan && (
        <PlanCheckoutModal
          open
          onOpenChange={open => { if (!open) closeCheckout(); }}
          defaultPlan={selectedPlan}
          subscription={subscription}
        />
      )}
      {renewalModalOpen && (
        <PlanCheckoutModal
          open={renewalModalOpen}
          onOpenChange={setRenewalModalOpen}
          defaultPlan={
            subscription?.planKey === 'START' || subscription?.planKey === 'PREMIUM'
              ? subscription.planKey
              : 'PRO'
          }
          subscription={subscription}
        />
      )}
    </div>
  );
}
